import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  Modal,
  Steps,
  Button,
  Upload,
  Typography,
  Select,
  Table,
  Progress,
  Alert,
  Space,
  message,
  Spin,
  Grid,
  Segmented,
} from "antd";
import {
  InboxOutlined,
  DownloadOutlined,
  ArrowRightOutlined,
  SafetyOutlined,
  OpenAIOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import {
  previewRentRoll,
  commitRentRoll,
  aiMapRentRoll,
} from "../lib/api/rentRollImportApi";
import { propertyKeys } from "../lib/queryKeys";
import { device } from "../lib/breakpoints";

const { Text, Paragraph } = Typography;

/** Compact body copy for step 1 across breakpoints. */
const UploadIntroParagraph = styled(Paragraph)`
  margin-bottom: 0 !important;
  color: var(--color-text-light);
  line-height: 1.55 !important;
  font-size: 0.875rem !important;
  @media (max-width: 767px) {
    padding: 0 0.5rem;
  }
`;
const { Dragger } = Upload;
const { useBreakpoint } = Grid;

/** Aligns with manual Add Unit + Add Tenant flows (required vs optional). */
const FIELD_SECTIONS = [
  {
    title: "Required (every row)",
    description: null,
    fields: [
      {
        key: "unit_number",
        label: "Unit number",
        hint: "Door / apt / suite ID (e.g. 101, 2B)",
      },
      { key: "tenant_name", label: "Tenant name", hint: "Full name" },
      { key: "tenant_email", label: "Tenant email", hint: "Unique per tenant" },
      { key: "monthly_rent", label: "Monthly rent", hint: "Numbers or $1,500" },
    ],
  },
  {
    title: "Property / site",
    description:
      "Map Property name (one column), or map all four address columns (street, city, state, zip).",
    fields: [
      {
        key: "property_name",
        label: "Property name (one line)",
        hint: "Building or full address in one column",
      },
      {
        key: "address_street",
        label: "Street address",
        hint: "Use with city, state, zip if you do not use property name",
      },
      { key: "address_city", label: "City", hint: "" },
      { key: "address_state", label: "State", hint: "e.g. IL" },
      { key: "address_zip", label: "ZIP", hint: "" },
    ],
  },
  {
    title: "Optional",
    description:
      "Matches manual door and lease fields when your file includes them.",
    fields: [
      {
        key: "door_name",
        label: "Door / unit label",
        hint: "Display name for the door",
      },
      { key: "bedrooms", label: "Bedrooms", hint: "Number" },
      { key: "bathrooms", label: "Bathrooms", hint: "Number (e.g. 1.5)" },
      {
        key: "tenant_phone",
        label: "Tenant phone",
        hint: "10 digits or formatted",
      },
      {
        key: "lease_start_date",
        label: "Lease start",
        hint: "ISO or MM/DD/YYYY",
      },
      { key: "lease_end_date", label: "Lease end", hint: "ISO or MM/DD/YYYY" },
      {
        key: "lease_status",
        label: "Lease status",
        hint: "Active, Pending, Inactive, Evicted",
      },
    ],
  },
];

/**
 * LandlordOps template (any CSV with these headers works). The importer also auto-maps
 * AppFolio Rent Roll exports and Buildium rent roll / units+residents exports - pick the
 * source below so column priority matches that platform.
 */
const TEMPLATE_CSV = `property_name,unit_number,door_name,address_street,address_city,address_state,address_zip,tenant_name,tenant_email,tenant_phone,monthly_rent,bedrooms,bathrooms,lease_start_date,lease_end_date,lease_status
"123 Main St",101,Front Door,,,,,Jane Doe,jane@example.com,5551234567,1500,2,1,01/01/2025,12/31/2025,Active
,202,,456 Oak Avenue,Springfield,IL,62701,John Smith,john@example.com,5559876543,1850,1,1,03/01/2025,02/28/2026,Active`;

/** Title + close in one flex row so the X stays vertically centered with the title on all breakpoints */
const ModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-width: 0;
`;

const ModalHeaderCloseBtn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin: 0;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: #fff !important;
  cursor: pointer;
  transition:
    background 0.2s,
    border-color 0.2s,
    opacity 0.2s;
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.55);
    color: #fff !important;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
  .anticon {
    font-size: 16px;
  }
`;

const ModalBody = styled.div`
  min-height: 0;
`;

/** Fills .ant-modal-body flex area; steps stay fixed above the scroll region */
const ImportModalBodyLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
`;

/** Only step content scrolls - avoids a body scrollbar running alongside the steps */
const ImportModalContentScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const StepsWrap = styled.div`
  flex-shrink: 0;
  margin-bottom: 1.75rem;
  /* overflow-x: auto forces overflow-y to compute to auto in CSS; hide Y to avoid a stray vertical bar */
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0 4px 8px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  width: 100%;
  /* Keep steps grouped in the center instead of stretching across full width */
  .ant-steps.ant-steps-horizontal {
    justify-content: center;
    flex: 0 1 auto;
    max-width: 100%;
  }
  .ant-steps.ant-steps-horizontal .ant-steps-item {
    flex: 0 1 auto !important;
    padding-inline-start: 6px !important;
    padding-inline-end: 6px !important;
  }
  .ant-steps.ant-steps-horizontal .ant-steps-item:first-child {
    padding-inline-start: 0 !important;
  }
  .ant-steps.ant-steps-horizontal .ant-steps-item:last-child {
    padding-inline-end: 0 !important;
  }
  /* antd small Steps: lock icon box + flex-center the digit (font metrics otherwise sit high) */
  .ant-steps.ant-steps-small .ant-steps-item-icon {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    line-height: 0 !important;
  }
  .ant-steps.ant-steps-small .ant-steps-item-icon > .ant-steps-icon {
    position: relative !important;
    top: 0 !important;
    left: 0 !important;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    margin: 0 !important;
    font-size: 12px !important;
    font-weight: 600;
    line-height: 1 !important;
    /* Optical center for tabular / lining numerals in a circle */
    transform: translateY(0.5px);
  }
  .ant-steps-item-title {
    font-size: 0.8125rem;
  }
  .ant-steps-item-description {
    font-size: 0.6875rem !important;
    margin-top: 2px !important;
  }
  /* Keep completed/current step visuals aligned with app primary brand */
  .ant-steps-item-finish .ant-steps-item-icon {
    background: var(--color-primary, #2c5f2d) !important;
    border-color: var(--color-primary, #2c5f2d) !important;
  }
  .ant-steps-item-finish .ant-steps-item-icon > .ant-steps-icon {
    color: #fff !important;
    transform: none;
  }
  .ant-steps-item-process .ant-steps-item-icon {
    background: #000 !important;
    border-color: #000 !important;
  }
  .ant-steps-item-process .ant-steps-item-icon > .ant-steps-icon {
    color: #fff !important;
    transform: none;
  }
  .ant-steps-item-finish .ant-steps-item-tail::after {
    background-color: var(--color-primary, #2c5f2d) !important;
  }
  /* Mobile: fit three steps in viewport - no horizontal scroll */
  @media (max-width: 767px) {
    margin-bottom: 1.25rem;
    padding: 0 0 8px;
    overflow-x: hidden;
    .ant-steps.ant-steps-horizontal {
      width: 100%;
      max-width: 100%;
    }
    .ant-steps.ant-steps-horizontal.ant-steps-label-vertical.ant-steps-small {
      width: 100%;
      max-width: 100%;
    }
    .ant-steps.ant-steps-horizontal .ant-steps-item {
      flex: 1 1 0 !important;
      min-width: 0 !important;
      padding-inline-start: 2px !important;
      padding-inline-end: 2px !important;
    }
    .ant-steps.ant-steps-horizontal .ant-steps-item:first-child {
      padding-inline-start: 0 !important;
    }
    .ant-steps.ant-steps-horizontal .ant-steps-item:last-child {
      padding-inline-end: 0 !important;
    }
    .ant-steps.ant-steps-small .ant-steps-item-icon {
      width: 22px !important;
      height: 22px !important;
      line-height: 22px !important;
      font-size: 11px !important;
    }
    .ant-steps.ant-steps-small .ant-steps-item-icon > .ant-steps-icon {
      font-size: 11px !important;
    }
    .ant-steps.ant-steps-horizontal.ant-steps-label-vertical
      .ant-steps-item-tail {
      top: 11px !important;
      margin-inline-start: 11px !important;
    }
    .ant-steps.ant-steps-horizontal.ant-steps-label-vertical
      .ant-steps-item-title {
      font-size: 0.6875rem;
      line-height: 1.2;
      font-weight: 600;
      white-space: normal !important;
      text-align: center;
      max-width: 100%;
      padding-inline: 0;
    }
  }
`;

const DraggerWrap = styled.div`
  width: 100%;
  overflow: visible;
  /* Prevent drag zone from stretching to fill flex parents */
  .ant-upload-wrapper {
    width: 100%;
    overflow: visible;
  }
  .ant-upload.ant-upload-drag {
    border-radius: 12px;
    background: var(--color-bg-tertiary, #f8faf8);
    border: 2px dashed var(--color-border-medium, #cbd5e1);
    transition:
      border-color 0.2s,
      background 0.2s;
    height: auto !important;
    min-height: 10.5rem;
    box-sizing: border-box;
    padding: 1.5rem 1.35rem 1.75rem;
    text-align: center;
    overflow: visible;
  }
  .ant-upload.ant-upload-drag .ant-upload {
    padding: 0.35rem 0.5rem 0.15rem;
    height: auto !important;
    min-height: 0;
    overflow: visible;
  }
  .ant-upload.ant-upload-drag .ant-upload-btn {
    height: auto !important;
    min-height: 0;
    display: block;
    width: 100%;
    overflow: visible;
  }
  .ant-upload-drag-container {
    outline: none;
    display: block !important;
    width: 100%;
    vertical-align: unset;
    overflow: visible;
  }
  .ant-upload.ant-upload-drag:hover {
    border-color: var(--color-primary);
    background: rgba(44, 95, 45, 0.06);
  }
  p.ant-upload-drag-icon {
    margin-bottom: 0.875rem !important;
    line-height: 1;
    text-align: center;
  }
  .ant-upload-drag-icon .anticon {
    font-size: 1.85rem;
    color: var(--color-primary);
  }
  p.ant-upload-text {
    margin: 0 0 0.625rem 0 !important;
    font-size: 0.9375rem !important;
    line-height: 1.5 !important;
    text-align: center !important;
    width: 100%;
    padding: 0 0.25rem;
    box-sizing: border-box;
  }
  p.ant-upload-hint {
    margin: 0 !important;
    padding: 0 0.5rem 0.125rem !important;
    font-size: 0.8125rem !important;
    line-height: 1.55 !important;
    color: var(--color-text-light, #64748b) !important;
    text-align: center !important;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
`;

/** Intro copy + template button */
const UploadIntroBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1.5rem;
  width: 100%;
`;

/** Step 1: vertical rhythm between alert, instructions, and drop zone */
const StepOneStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  padding-bottom: 0.75rem;
  box-sizing: border-box;
`;

/** Step 2 mapping page stack with steady section rhythm */
const StepMapStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  padding-bottom: 0.5rem;
  box-sizing: border-box;
`;

const MapFieldSection = styled.div`
  width: 100%;
  border: 1px solid var(--color-border-light, #e8ecf0);
  border-radius: 12px;
  background: var(--color-bg-secondary, #f9fafb);
  padding: 14px 14px 8px;
  box-sizing: border-box;
`;

const PreviewSection = styled.div`
  width: 100%;
  border: 1px solid var(--color-border-light, #e8ecf0);
  border-radius: 12px;
  background: #fff;
  padding: 12px;
  box-sizing: border-box;
`;

/** Separates template from drop zone (divider + air above the dragger) */
const DropZoneSection = styled.div`
  width: 100%;
  padding-top: 1.125rem;
  border-top: 1px solid var(--color-border-light, #e8ecf0);
  box-sizing: border-box;
`;

/** Back + Run import (replaces removed modal footer on map step) */
const ImportMapStepActions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--color-border-light, #e8ecf0);
  width: 100%;
  box-sizing: border-box;
  .ant-btn-primary {
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border: none;
    border-radius: 8px;
    font-weight: 600;
  }
  .ant-btn-primary {
    min-width: 180px;
  }
  @media (max-width: 768px) {
    .ant-btn-primary {
      width: 100%;
      min-width: 0;
    }
  }
`;

/** Prominent template action - readable on white */
const TemplateDownloadRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const TemplateDownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 0;
  padding: 0.625rem 1.25rem;
  border: 1px solid rgba(44, 95, 45, 0.35);
  border-radius: 10px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(44, 95, 45, 0.06) 100%
  );
  box-shadow: 0 1px 3px rgba(44, 95, 45, 0.08);
  cursor: pointer;
  font-family: var(--font-secondary);
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-primary, #2c5f2d);
  transition:
    border-color 0.2s,
    background 0.2s,
    box-shadow 0.2s,
    color 0.2s;
  &:hover {
    border-color: var(--color-primary, #2c5f2d);
    background: rgba(44, 95, 45, 0.1);
    color: var(--color-primary-dark, #1a3d1b);
    box-shadow: 0 2px 10px rgba(44, 95, 45, 0.14);
  }
  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
  &:active {
    transform: translateY(1px);
  }
  .anticon {
    font-size: 1.1rem;
    color: var(--color-primary, #2c5f2d);
  }
`;

/* Matches Dashboard StyledDscrModal / AddExpenseModal branding */
const StyledImportPortfolioModal = styled(Modal)`
  .ant-modal-header {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 16px 20px;
  }
  .ant-modal-header,
  .ant-modal-header * {
    color: #fff !important;
  }
  .ant-modal-title {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-weight: 600;
    font-size: 18px;
    line-height: 1.35;
  }
  .ant-modal-body {
    padding: 24px 24px 28px;
    background: var(--color-bg-primary, #fff);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1 1 auto;
    /* Desktop/tablet: tall body for map step + preview table */
    max-height: min(90vh, 840px);
    font-family: var(--font-secondary);
    font-size: 0.9375rem;
    line-height: 1.6;
    color: var(--color-text, #1f2937);
  }
  /* Spin must not clip tall upload dragger content */
  .ant-modal-body .ant-spin-nested-loading .ant-spin-container {
    overflow: visible !important;
  }
  /*
 * Rent-roll safety Alert: this antd version does not apply Alert styles to the DOM.
 * Compact headline and supporting copy; child spans inherit instead of taking
 * the global body span size.
 */
  .ant-modal-body .import-portfolio-safety-alert .ant-alert-message {
    font-size: 0.95rem !important;
    font-weight: 700 !important;
    line-height: 1.35;
    margin-bottom: 6px;
    color: var(--color-text, #1f2937);
  }
  .ant-modal-body .import-portfolio-safety-alert .ant-alert-description {
    font-size: 0.8125rem !important;
    line-height: 1.55;
    font-weight: 500;
    color: var(--color-text, #1f2937);
  }
  .ant-modal-body .import-portfolio-safety-alert .ant-alert-description span,
  .ant-modal-body .import-portfolio-safety-alert .ant-alert-description strong {
    font-size: inherit !important;
    line-height: inherit !important;
  }
  .ant-modal-body a.ant-btn-link,
  .ant-modal-body .ant-btn-link {
    color: var(--color-primary, #2c5f2d) !important;
  }
  /* Prevent nested vertical scrolling in preview table; outer modal content handles Y scroll */
  .ant-modal-body .import-portfolio-preview-table .ant-table-body {
    max-height: none !important;
    overflow-y: visible !important;
  }
  .ant-modal-body .import-portfolio-preview-table .ant-table-content {
    overflow-y: visible !important;
  }
  .ant-modal-body a.ant-btn-link:hover,
  .ant-modal-body .ant-btn-link:hover {
    color: var(--color-primary-dark, #1a3d1b) !important;
  }
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    padding: 0;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 32px);
  }
  @media (max-width: 768px) {
    .ant-modal-wrap {
      padding: 12px 0;
      align-items: flex-start;
    }
    .ant-modal {
      max-width: 95vw !important;
      width: 95vw !important;
      margin: 0 auto;
      padding-bottom: 12px;
    }
    .ant-modal-content {
      display: flex;
      flex-direction: column;
      max-height: calc(100vh - 24px);
    }
    .ant-modal-header {
      flex-shrink: 0;
    }
    .ant-modal-body {
      flex: 1 1 auto;
      min-height: 0;
      max-height: none;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      padding: 16px;
    }
  }
  @media (max-width: 480px) {
    .ant-modal-wrap {
      padding: 8px 0 12px;
    }
    .ant-modal {
      max-width: 98vw !important;
      width: 98vw !important;
      margin: 0 auto;
    }
    .ant-modal-content {
      max-height: calc(100vh - 20px);
      border-radius: 12px;
    }
    .ant-modal-body {
      padding: 12px 16px;
    }
  }
`;

function downloadTemplate() {
  const blob = new Blob([TEMPLATE_CSV], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "landlordops-rent-roll-template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function normalizeHeader(s) {
  return String(s || "")
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

/** Map normalized header string → original CSV column label */
function headersByNormalized(headers) {
  const m = new Map();
  for (const h of headers) {
    m.set(normalizeHeader(h), h);
  }
  return m;
}

function firstHeader(byNorm, keys) {
  for (const k of keys) {
    const col = byNorm.get(k);
    if (col) return col;
  }
  return undefined;
}

/**
 * AppFolio / Buildium exports use different canonical names; these overrides pick the
 * right column when multiple headers could match (e.g. Monthly Rent vs Market Rent).
 */
function mappingOverridesForFormat(headers, format) {
  const by = headersByNormalized(headers);
  if (format === "appfolio") {
    return {
      unit_number: firstHeader(by, [
        "unit",
        "unit_name",
        "suite",
        "space",
        "apartment",
        "door",
        "apt",
        "unit_number",
      ]),
      property_name: firstHeader(by, [
        "property",
        "property_name",
        "building",
        "building_name",
        "site",
        "location",
        "community",
      ]),
      tenant_name: firstHeader(by, [
        "tenant",
        "tenant_name",
        "resident",
        "resident_name",
        "lease_holder",
        "occupant",
        "name",
      ]),
      tenant_email: firstHeader(by, [
        "email",
        "e_mail",
        "tenant_email",
        "contact_email",
        "primary_email",
        "email_address",
      ]),
      tenant_phone: firstHeader(by, [
        "phone",
        "mobile",
        "cell",
        "tenant_phone",
        "primary_phone",
        "home_phone",
      ]),
      monthly_rent: firstHeader(by, [
        "monthly_rent",
        "rent",
        "posted_rent",
        "actual_rent",
        "lease_rent",
        "total_rent",
        "market_rent",
      ]),
      lease_start_date: firstHeader(by, [
        "lease_start",
        "lease_from",
        "lease_start_date",
        "start_date",
        "move_in_date",
        "lease_begin",
      ]),
      lease_end_date: firstHeader(by, [
        "lease_end",
        "lease_to",
        "lease_end_date",
        "end_date",
        "move_out_date",
        "lease_expiration",
        "lease_expiry",
      ]),
      lease_status: firstHeader(by, [
        "lease_status",
        "status",
        "tenant_status",
        "occupancy",
        "occupancy_status",
      ]),
      bedrooms: firstHeader(by, ["bedrooms", "beds", "br", "of_bedrooms"]),
      bathrooms: firstHeader(by, ["bathrooms", "baths", "ba", "of_bathrooms"]),
      address_street: firstHeader(by, [
        "unit_street_address_1",
        "street_address",
        "address_line_1",
        "address_street",
        "address1",
        "addr1",
      ]),
      address_city: firstHeader(by, ["unit_city", "city", "address_city"]),
      address_state: firstHeader(by, [
        "unit_state",
        "state",
        "address_state",
        "st",
      ]),
      address_zip: firstHeader(by, [
        "unit_zip",
        "zip",
        "zipcode",
        "postal_code",
        "address_zip",
      ]),
    };
  }
  if (format === "buildium") {
    return {
      unit_number: firstHeader(by, [
        "unit_number",
        "unit",
        "suite",
        "space",
        "apartment",
        "door",
      ]),
      address_street: firstHeader(by, [
        "unit_address_line_1",
        "address_line_1",
        "street_address",
        "property_address",
        "address_street",
        "address1",
        "addr1",
      ]),
      address_city: firstHeader(by, [
        "city_locality",
        "city",
        "address_city",
        "unit_city",
      ]),
      address_state: firstHeader(by, [
        "state_province_territory",
        "state",
        "address_state",
        "st",
        "unit_state",
      ]),
      address_zip: firstHeader(by, [
        "postal_code",
        "zip",
        "zip_code",
        "address_zip",
        "unit_zip",
      ]),
      property_name: firstHeader(by, [
        "property_name",
        "property",
        "building",
        "building_name",
        "site",
      ]),
      tenant_name: firstHeader(by, [
        "tenant_name",
        "tenant",
        "resident",
        "primary_tenant",
        "resident_name",
        "fullname",
        "name",
      ]),
      tenant_email: firstHeader(by, [
        "email",
        "e_mail",
        "tenant_email",
        "primary_email",
        "email_address",
        "contact_email",
      ]),
      tenant_phone: firstHeader(by, [
        "phone",
        "mobile",
        "cell",
        "tenant_phone",
        "primary_phone",
      ]),
      monthly_rent: firstHeader(by, [
        "rent",
        "monthly_rent",
        "lease_rent",
        "amount",
        "total_rent",
        "posted_rent",
        "actual_rent",
        "market_rent",
      ]),
      lease_start_date: firstHeader(by, [
        "lease_start",
        "lease_start_date",
        "start_date",
        "lease_from",
        "move_in_date",
      ]),
      lease_end_date: firstHeader(by, [
        "lease_end",
        "lease_end_date",
        "end_date",
        "lease_to",
        "lease_expiration",
      ]),
      lease_status: firstHeader(by, [
        "lease_status",
        "status",
        "tenant_status",
        "occupancy_status",
      ]),
      bedrooms: firstHeader(by, ["bedrooms", "beds", "br"]),
      bathrooms: firstHeader(by, ["bathrooms", "baths", "ba"]),
    };
  }
  return {};
}

function guessMapping(headers) {
  const m = {};
  const candidates = {
    property_name: [
      "property_name",
      "property",
      "building",
      "building_name",
      "address",
      "property_address",
      "site",
      "location",
      "community",
    ],
    address_street: [
      "address_street",
      "street",
      "address_line_1",
      "unit_street_address_1",
      "unit_address_line_1",
      "address1",
      "addr1",
      "street_address",
    ],
    address_city: ["address_city", "city", "city_locality", "unit_city"],
    address_state: [
      "address_state",
      "state",
      "state_province_territory",
      "st",
      "unit_state",
    ],
    address_zip: [
      "address_zip",
      "zip",
      "zipcode",
      "zip_code",
      "postal_code",
      "unit_zip",
    ],
    unit_number: [
      "unit_number",
      "unit",
      "suite",
      "door",
      "apt",
      "apartment",
      "space",
      "unit_name",
    ],
    door_name: ["door_name", "door_label", "unit_name", "unit_label"],
    tenant_name: [
      "tenant_name",
      "tenant",
      "resident",
      "resident_name",
      "lessee",
      "primary_tenant",
      "name",
    ],
    tenant_email: [
      "tenant_email",
      "email",
      "e_mail",
      "contact_email",
      "primary_email",
      "email_address",
    ],
    tenant_phone: [
      "tenant_phone",
      "phone",
      "mobile",
      "cell",
      "primary_phone",
      "home_phone",
    ],
    monthly_rent: [
      "monthly_rent",
      "rent",
      "base_rent",
      "lease_rent",
      "posted_rent",
      "actual_rent",
      "total_rent",
      "market_rent",
      "amount",
    ],
    bedrooms: ["bedrooms", "beds", "br", "of_bedrooms"],
    bathrooms: ["bathrooms", "baths", "ba", "of_bathrooms"],
    lease_start_date: [
      "lease_start_date",
      "lease_start",
      "start_date",
      "lease_begin",
      "lease_from",
      "move_in_date",
    ],
    lease_end_date: [
      "lease_end_date",
      "lease_end",
      "end_date",
      "lease_expiry",
      "lease_to",
      "lease_expiration",
      "move_out_date",
    ],
    lease_status: [
      "lease_status",
      "status",
      "tenant_status",
      "occupancy_status",
      "occupancy",
    ],
  };

  for (const field of Object.keys(candidates)) {
    for (const h of headers) {
      const n = normalizeHeader(h);
      if (candidates[field].includes(n)) {
        m[field] = h;
        break;
      }
    }
  }
  return m;
}

function buildMapping(headers, format) {
  const base = guessMapping(headers);
  if (!format || format === "generic") return base;
  const over = mappingOverridesForFormat(headers, format);
  const out = { ...base };
  for (const [k, v] of Object.entries(over)) {
    if (v) out[k] = v;
  }
  return out;
}

function isRentRollMappingComplete(mapping) {
  const req = ["unit_number", "tenant_name", "tenant_email", "monthly_rent"];
  for (const k of req) {
    if (!mapping[k] || !String(mapping[k]).trim()) return false;
  }
  const hasName = mapping.property_name && String(mapping.property_name).trim();
  const hasFull =
    mapping.address_street?.trim() &&
    mapping.address_city?.trim() &&
    mapping.address_state?.trim() &&
    mapping.address_zip?.trim();
  return !!(hasName || hasFull);
}

const ImportPortfolioModal = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const screens = useBreakpoint();
  const isNarrow = screens.md === false;
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [loadingAiMapping, setLoadingAiMapping] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingCommit, setLoadingCommit] = useState(false);
  const [commitPercent, setCommitPercent] = useState(0);
  const [result, setResult] = useState(null);
  const [importFormat, setImportFormat] = useState("generic");
  const importFormatRef = useRef(importFormat);
  importFormatRef.current = importFormat;

  const modalWidth = isNarrow ? "calc(100vw - 16px)" : 720;

  const reset = () => {
    setStep(0);
    setFile(null);
    setPreview(null);
    setMapping({});
    setImportFormat("generic");
    setLoadingAiMapping(false);
    setLoadingPreview(false);
    setLoadingCommit(false);
    setCommitPercent(0);
    setResult(null);
  };

  const handleClose = () => {
    if (loadingCommit || loadingPreview) return;
    reset();
    onClose?.();
  };

  const closeDisabled = loadingCommit || loadingPreview;

  const onSelectFile = useCallback(async (f) => {
    setFile(f);
    setPreview(null);
    setMapping({});
    setResult(null);
    setLoadingPreview(true);
    setCommitPercent(0);
    try {
      const data = await previewRentRoll(f);
      if (data.mode !== "preview" || !data.headers) {
        throw new Error("Unexpected preview response");
      }
      setPreview(data);
      setMapping(buildMapping(data.headers, importFormatRef.current));
      setStep(1);
      message.success("File parsed - confirm column mapping");
    } catch (e) {
      message.error(e?.message || "Could not read CSV");
      setFile(null);
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  useEffect(() => {
    if (!preview?.headers?.length) return;
    setMapping(buildMapping(preview.headers, importFormat));
  }, [importFormat, preview?.headers]);

  const runAiMapping = useCallback(async () => {
    if (!file) return;
    setLoadingAiMapping(true);
    try {
      const data = await aiMapRentRoll(file);
      if (data?.mode !== "ai_mapping" || !data?.mapping) {
        throw new Error("AI mapping returned an unexpected response");
      }
      setMapping(data.mapping);
      message.success("AI mapped your CSV columns");
    } catch (e) {
      message.error(e?.message || "AI mapping failed");
    } finally {
      setLoadingAiMapping(false);
    }
  }, [file]);

  const mappingComplete = useMemo(
    () => isRentRollMappingComplete(mapping),
    [mapping]
  );

  const runCommit = async () => {
    if (!file || !mappingComplete) return;
    setLoadingCommit(true);
    setCommitPercent(10);
    const timer = setInterval(() => {
      setCommitPercent((p) => (p < 90 ? p + 12 : p));
    }, 400);
    try {
      const data = await commitRentRoll(file, mapping);
      setCommitPercent(100);
      setResult(data);
      setStep(2);
      message.success("Import complete");
      queryClient.invalidateQueries({ queryKey: propertyKeys.all });
    } catch (e) {
      message.error(e?.message || "Import failed");
    } finally {
      clearInterval(timer);
      setLoadingCommit(false);
    }
  };

  const previewColumns = useMemo(() => {
    if (!preview?.headers?.length) return [];
    return preview.headers.map((h) => ({
      title: h,
      dataIndex: h,
      key: h,
      ellipsis: true,
    }));
  }, [preview]);

  const previewDataSource = useMemo(() => {
    if (!preview?.previewRows) return [];
    return preview.previewRows.map((row, i) => ({ ...row, key: i }));
  }, [preview]);

  const safetyAlert = useMemo(
    () => (
      <Alert
        type="success"
        showIcon
        className="import-portfolio-safety-alert"
        icon={<SafetyOutlined />}
        message={
          isNarrow ? (
            <>
              Your existing data
              <br />
              stays safe
            </>
          ) : (
            "Your existing data stays safe"
          )
        }
        description={
          <span>
            This import only <strong>adds</strong> new records. We never
            overwrite or edit existing properties, doors, or tenants. Rows that
            match an email or door already in your portfolio are skipped so
            nothing gets replaced.
          </span>
        }
        style={{ marginBottom: 0 }}
      />
    ),
    [isNarrow]
  );

  return (
    <StyledImportPortfolioModal
      title={
        <ModalTitleRow>
          <span style={{ flex: 1, minWidth: 0 }}>
            Import portfolio from CSV
          </span>
          <ModalHeaderCloseBtn
            type="button"
            onClick={handleClose}
            disabled={closeDisabled}
            aria-label="Close"
          >
            <CloseOutlined />
          </ModalHeaderCloseBtn>
        </ModalTitleRow>
      }
      closable={false}
      open={open}
      onCancel={handleClose}
      width={modalWidth}
      centered
      destroyOnClose
      maskClosable={!closeDisabled}
      keyboard={!closeDisabled}
      footer={null}
      wrapClassName="import-portfolio-modal-wrap"
    >
      <ImportModalBodyLayout>
        <StepsWrap>
          <Steps
            current={step}
            size="small"
            direction="horizontal"
            labelPlacement={isNarrow ? "vertical" : "horizontal"}
            responsive={false}
            items={[
              {
                title: "Upload",
                description: isNarrow ? undefined : "CSV file",
              },
              {
                title: "Map columns",
                description: isNarrow ? undefined : "Match fields",
              },
              { title: "Done", description: isNarrow ? undefined : "Summary" },
            ]}
          />
        </StepsWrap>

        <ImportModalContentScroll>
          <ModalBody>
            {step === 0 && (
              <StepOneStack>
                {safetyAlert}
                <UploadIntroBlock>
                  <UploadIntroParagraph>
                    Upload a rent roll CSV to create properties, doors, and
                    tenants in bulk. Maximum <strong>2,000</strong> data rows
                    per file. We match <strong>AppFolio</strong> and{" "}
                    <strong>Buildium</strong> export headers automatically when
                    you choose the source on the mapping step. You can also use
                    our template or generic mapping.
                  </UploadIntroParagraph>
                  <TemplateDownloadRow>
                    <TemplateDownloadButton
                      type="button"
                      onClick={downloadTemplate}
                    >
                      <DownloadOutlined aria-hidden />
                      Download CSV template
                    </TemplateDownloadButton>
                  </TemplateDownloadRow>
                </UploadIntroBlock>
                <DropZoneSection>
                  <Spin spinning={loadingPreview} tip="Reading CSV…">
                    <DraggerWrap>
                      <Dragger
                        style={{ height: "auto" }}
                        accept=".csv,text/csv"
                        maxCount={1}
                        showUploadList={!!file}
                        fileList={
                          file
                            ? [
                                {
                                  uid: "1",
                                  name: file.name,
                                  status: "done",
                                },
                              ]
                            : []
                        }
                        beforeUpload={(f) => {
                          onSelectFile(f);
                          return false;
                        }}
                        onRemove={() => {
                          setFile(null);
                          setPreview(null);
                          setStep(0);
                          return true;
                        }}
                        disabled={loadingPreview}
                      >
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p
                          className="ant-upload-text"
                          style={{ fontWeight: 600 }}
                        >
                          {isNarrow
                            ? "Tap to upload a CSV"
                            : "Drop a CSV here, or click to browse"}
                        </p>
                        <p
                          className="ant-upload-hint"
                          style={{ fontSize: "0.875rem" }}
                        >
                          One row per occupied door. You will map columns in the
                          next step.
                        </p>
                      </Dragger>
                    </DraggerWrap>
                  </Spin>
                </DropZoneSection>
              </StepOneStack>
            )}

            {step === 1 && preview && (
              <StepMapStack>
                {safetyAlert}
                <Alert
                  type="info"
                  showIcon
                  message={`${preview.totalRows} row(s) found`}
                  description="Match each LandlordOps field to a column from your file. Preview the first rows below."
                />
                <div style={{ marginTop: 12, marginBottom: 16 }}>
                  <Text strong style={{ display: "block", marginBottom: 8 }}>
                    Export source (column priority)
                  </Text>
                  <Segmented
                    block={isNarrow}
                    value={importFormat}
                    onChange={(v) => setImportFormat(v)}
                    options={[
                      { label: "Generic / template", value: "generic" },
                      { label: "AppFolio export", value: "appfolio" },
                      { label: "Buildium export", value: "buildium" },
                    ]}
                  />
                  <Paragraph
                    type="secondary"
                    style={{
                      marginTop: 10,
                      marginBottom: 0,
                      fontSize: "0.8125rem",
                    }}
                  >
                    {importFormat === "appfolio" &&
                      "Uses AppFolio Rent Roll style headers (e.g. Unit, Property, Monthly Rent before Market Rent)."}
                    {importFormat === "buildium" &&
                      "Uses Buildium-style unit + address columns (e.g. Unit address line 1, City/Locality, Postal code)."}
                    {importFormat === "generic" &&
                      "Best for our template or mixed files. Still recognizes common synonyms."}
                  </Paragraph>
                </div>
                {!mappingComplete && (
                  <div
                    style={{ width: "100%", marginTop: -4, marginBottom: 8 }}
                  >
                    <Button
                      type="default"
                      onClick={runAiMapping}
                      loading={loadingAiMapping}
                      disabled={loadingCommit || loadingPreview}
                      icon={<OpenAIOutlined />}
                      style={{
                        borderRadius: 10,
                        border: "1px solid var(--color-primary, #2c5f2d)",
                      }}
                    >
                      Auto-map with AI
                    </Button>
                  </div>
                )}
                {FIELD_SECTIONS.map((section) => (
                  <MapFieldSection key={section.title}>
                    <Text strong style={{ display: "block", marginBottom: 4 }}>
                      {section.title}
                    </Text>
                    {section.description && (
                      <Paragraph
                        type="secondary"
                        style={{ marginBottom: 12, fontSize: "0.875rem" }}
                      >
                        {section.description}
                      </Paragraph>
                    )}
                    {section.fields.map((f) => (
                      <div key={f.key} style={{ marginBottom: 14 }}>
                        <Text strong>{f.label}</Text>
                        {f.hint ? (
                          <Text type="secondary" style={{ marginLeft: 8 }}>
                            ({f.hint})
                          </Text>
                        ) : null}
                        <Select
                          style={{ width: "100%", marginTop: 6 }}
                          allowClear
                          placeholder="Select CSV column"
                          value={mapping[f.key] || undefined}
                          onChange={(v) =>
                            setMapping((prev) => ({
                              ...prev,
                              [f.key]: v || undefined,
                            }))
                          }
                          options={preview.headers.map((h) => ({
                            value: h,
                            label: h,
                          }))}
                          showSearch
                          optionFilterProp="label"
                          size="large"
                        />
                      </div>
                    ))}
                  </MapFieldSection>
                ))}
                <PreviewSection>
                  <Text strong>Preview</Text>
                  <Table
                    size="small"
                    className="import-portfolio-preview-table"
                    scroll={{ x: "max-content" }}
                    style={{ marginTop: 8 }}
                    columns={previewColumns}
                    dataSource={previewDataSource}
                    pagination={false}
                  />
                </PreviewSection>
                {loadingCommit && (
                  <Progress percent={commitPercent} status="active" />
                )}
                <ImportMapStepActions>
                  <Button
                    type="primary"
                    icon={
                      <ArrowRightOutlined
                        style={{ filter: "brightness(0) invert(1)" }}
                      />
                    }
                    loading={loadingCommit}
                    disabled={!mappingComplete}
                    onClick={runCommit}
                    size="large"
                  >
                    Run import
                  </Button>
                </ImportMapStepActions>
              </StepMapStack>
            )}

            {step === 2 && result && (
              <Space
                direction="vertical"
                size="middle"
                style={{ width: "100%" }}
              >
                <Alert type="success" showIcon message="Import finished" />
                {result?.rowsSkippedByPlan > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message={
                      result.totalRowsInCsv != null
                        ? `Plan limit reached: imported ${
                            result.rowsImported ?? 0
                          } of ${result.totalRowsInCsv} row(s)`
                        : `Plan limit reached: imported ${
                            result.rowsImported ?? 0
                          } row(s)`
                    }
                    description={
                      result?.plan?.unitCap != null ? (
                        <span>
                          Your {result.plan.unitCap} unit plan limit was
                          reached. We skipped{" "}
                          <strong>{result.rowsSkippedByPlan}</strong> row(s) so
                          your portfolio stays within your subscription.
                        </span>
                      ) : (
                        <span>
                          We skipped <strong>{result.rowsSkippedByPlan}</strong>{" "}
                          row(s) to stay within your subscription.
                        </span>
                      )
                    }
                  />
                )}
                <div>
                  <Text>Properties created: </Text>
                  <Text strong>{result.propertiesCreated ?? 0}</Text>
                </div>
                <div>
                  <Text>Doors created: </Text>
                  <Text strong>{result.unitsCreated ?? 0}</Text>
                </div>
                <div>
                  <Text>Tenants created: </Text>
                  <Text strong>{result.tenantsCreated ?? 0}</Text>
                </div>
                {(() => {
                  const skipped = Array.isArray(result.skippedRows)
                    ? result.skippedRows
                    : [];
                  const planSkipped = result.rowsSkippedByPlan ?? 0;
                  const nonPlanSkipped = skipped.filter(
                    (s) =>
                      !String(s.reason || "").startsWith(
                        "Plan unit cap reached"
                      )
                  );
                  if (nonPlanSkipped.length === 0) return null;
                  return (
                    <Alert
                      type="warning"
                      showIcon
                      message={
                        planSkipped > 0
                          ? `${nonPlanSkipped.length} row(s) skipped (duplicates/invalid). Plan limit skipped: ${planSkipped}.`
                          : `${nonPlanSkipped.length} row(s) skipped (nothing was overwritten)`
                      }
                      description={
                        <ul style={{ margin: "8px 0 0 16px", padding: 0 }}>
                          {nonPlanSkipped.slice(0, 12).map((s, idx) => (
                            <li key={`${s.row}-${idx}`}>
                              Row {s.row}: {s.reason}
                            </li>
                          ))}
                          {nonPlanSkipped.length > 12 && <li>…and more</li>}
                        </ul>
                      }
                    />
                  );
                })()}
              </Space>
            )}
          </ModalBody>
        </ImportModalContentScroll>
      </ImportModalBodyLayout>
    </StyledImportPortfolioModal>
  );
};

export default ImportPortfolioModal;
